// KPIs de la pestaña de referidos: contados sobre lo cargado o, si falta leer, tomados del back.
import type { KpiItem } from '@/shared/patterns/KpiBar';
import { countLifecycleStatuses, lifecycleKpiItems } from '@/shared/patterns/Lifecycle';
import type { SalePromotionsState } from '../api/usePromotions';
import { listStatusOf } from './promotionsList';

/** Con más páginas por leer, los conteos salen del back; el que no trae se marca parcial («50+»). */
export function referralKpiItems(
    list: Pick<SalePromotionsState, 'items' | 'kpis' | 'hasNextPage' | 'isSuccess'>,
    enabled: boolean,
    now: Date,
): KpiItem[] {
    if (!enabled || !list.isSuccess) return lifecycleKpiItems(null);
    const loaded = countLifecycleStatuses(list.items.map((promo) => listStatusOf(promo, now)));
    if (!list.hasNextPage) return lifecycleKpiItems(loaded);
    const [active, scheduled, paused, draft] = lifecycleKpiItems(loaded);
    const partial = (item: KpiItem, count: number): KpiItem => ({
        ...item,
        value: `${String(count)}+`,
        title: 'Cuenta solo lo cargado: hay más promos por leer.',
    });
    const server = list.kpis;
    return [
        server ? { ...active, value: server.activeCount } : partial(active, loaded.active),
        server
            ? { ...scheduled, value: server.scheduledCount }
            : partial(scheduled, loaded.scheduled),
        server ? { ...paused, value: server.pausedCount } : partial(paused, loaded.paused),
        partial(draft, loaded.draft),
    ];
}

type ReferralListSummary = Pick<
    SalePromotionsState,
    'items' | 'kpis' | 'hasNextPage' | 'isSuccess'
>;

/** Junta las listas de varias cadenas en una: suma los conteos del back si todas los trajeron. */
export function mergeReferralLists(lists: readonly ReferralListSummary[]): ReferralListSummary {
    const allKpis = lists.every((list) => list.kpis !== undefined);
    return {
        items: lists.flatMap((list) => list.items),
        hasNextPage: lists.some((list) => list.hasNextPage),
        isSuccess: lists.length > 0 && lists.every((list) => list.isSuccess),
        kpis: allKpis
            ? lists.reduce(
                  (sum, list) => ({
                      ...sum,
                      activeCount: sum.activeCount + (list.kpis?.activeCount ?? 0),
                      scheduledCount: sum.scheduledCount + (list.kpis?.scheduledCount ?? 0),
                      pausedCount: sum.pausedCount + (list.kpis?.pausedCount ?? 0),
                      expiringIn7DaysCount:
                          sum.expiringIn7DaysCount + (list.kpis?.expiringIn7DaysCount ?? 0),
                      redemptionsLast30Days:
                          sum.redemptionsLast30Days + (list.kpis?.redemptionsLast30Days ?? 0),
                  }),
                  {
                      activeCount: 0,
                      scheduledCount: 0,
                      pausedCount: 0,
                      expiringIn7DaysCount: 0,
                      redemptionsLast30Days: 0,
                      budgetUsedBps: null,
                  },
              )
            : undefined,
    };
}
